import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import { Box, Button, Card, Grid, Link, TableCell, TableRow, TextField } from "@mui/material";
import queryString from "query-string";
import { formatInTimeZone } from "date-fns-tz";

import { SCREEN_PATHS } from "@APP/navigation";
import {
  AppState,
  clearOrgSearchResults,
  clearSearchResults,
  hideLoader,
  setOrganisationSearchResults,
  setSelectedUser,
  setUserSearchResults,
  showLoader,
} from "@APP/redux";
import { Page, ScreenHeader, Table } from "@APP/components";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { API } from "@APP/services";
import { filterToolbarStyles } from "@APP/styles";
import { tableStyles as useStyles } from "@APP/styles/table";
import { formatErrorMessage, getUserTimeZone } from "@APP/utils";
import {
  OrganisationSearchTerm,
  OrganisationSearchedData,
  UserSearchResponse,
  UserSearchTerms,
} from "@APP/types";
import { commonFormatDate } from "@APP/constants";

import OrganisationList from "../organisationDasboard/OrganisationList";

const UpdateUserView = () => {
  const alert = useAlert();
  const handleErrorCodes = useHandleErrorCodes();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const toolbarClasses = filterToolbarStyles();
  const [page, setPage] = useState<number>(0);
  const [entries, setEntries] = useState<number>(25);
  const [orgPage, setOrgPage] = useState<number>(0);
  const [orgEntries, setOrgEntries] = useState<number>(25);
  const [lastPage, setLastPage] = useState<number>(0);
  const [orgLastPage, setOrgLastPage] = useState<number>(0);
  const { results, orgResults } = useSelector(
    ({
      userAdmin: {
        users: { results },
      },
      organisationAdmin: {
        organisations: { orgResults },
      },
    }: AppState) => ({
      results,
      orgResults,
    }),
  );

  useEffect(() => {
    (() => {
      fetchSearchResults();
      handlePagination(page, entries);
    })();
  }, [page, entries]);

  useEffect(() => {
    (() => {
      fetchSearchResults();
      handlePagination(orgPage, orgEntries);
    })();
  }, [orgEntries, orgPage]);

  const handlePagination = (page: number, entries: number) => {
    const search = createQueryParamsString({ page, entries });
    history.push({
      pathname: history.location.pathname,
      search,
    });
  };

  const fetchSearchResults = async () => {
    try {
      dispatch(showLoader());
      const pagination = { page, entries };
      const orgPagination = { page: orgPage, entries: orgEntries };

      const { username, org } = values;
      let searchTerms = {} as UserSearchTerms;
      let orgSearchTerms = {} as OrganisationSearchTerm;

      // BE is case sensitive
      if (username?.length) searchTerms.username = username.toLowerCase();
      if (org?.length) orgSearchTerms.orgname = org.toLowerCase();

      searchTerms = Object.fromEntries(
        Object.entries(searchTerms).map(([key, value]) => [key, value.trim()]),
      );

      orgSearchTerms = Object.fromEntries(
        Object.entries(orgSearchTerms).map(([key, value]) => [key, value.trim()]),
      );

      if (Object.values(searchTerms).length) {
        const { data, meta } = await API.getSearchedUsers(searchTerms, pagination);

        if (meta.totalItems) {
          if (meta.totalItems <= entries) {
            setLastPage(1);
          } else {
            const lastPageIndex = Math.ceil(meta.totalItems / entries);
            setLastPage(lastPageIndex);
          }
        }

        dispatch(setUserSearchResults(data));
      }

      if (org?.length > 2) {
        const orgData: OrganisationSearchedData = await API.getSearchedOrganisations(
          orgSearchTerms,
          orgPagination,
        );
        dispatch(setOrganisationSearchResults(orgData));
        if (orgData?.meta?.totalItems) {
          if (orgData?.meta?.totalItems <= orgEntries) {
            setOrgLastPage(1);
          } else {
            const lastPageIndex = Math.ceil(orgData?.meta?.totalItems / orgEntries);
            setOrgLastPage(lastPageIndex);
          }
        }
      }
    } catch (error: any) {
      dispatch(setUserSearchResults([]));
      const errorData = error?.response?.data;
      const isHandled = handleErrorCodes(errorData?.errorCode);

      if (!isHandled) return;
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    touched,
    values,
    resetForm,
  } = useFormik({
    initialValues: {
      username: "",
      org: "",
      phone: "",
    },
    onSubmit: fetchSearchResults,
  });

  const createQueryParamsString = (params?: any) => {
    const queryStringResults = queryString.parse(history.location.search);

    // create search query url
    return Object.entries({
      page,
      entries,
      ...queryStringResults,
      ...params,
    })
      .filter((query) => {
        // filter out falsy values (except 0)
        return !!query[1] || query[1] === Number(0);
      })
      .reduce((acc, query, index) => {
        const [key, value] = query;

        if (index === 0) acc += `?${key}=${value}`;
        else acc += `&${key}=${value}`;

        return acc;
      }, "");
  };

  const handleOnEntriesChange = (entries: number) => {
    setPage(0);
    setEntries(entries);
    setLastPage(Math.ceil(results.length / entries));
  };

  const handleOrgOnEntriesChange = (entries: number) => {
    setOrgPage(0);
    setOrgEntries(entries);
    orgResults?.meta && setOrgLastPage(Math.ceil(orgResults?.meta?.totalItems / entries));
  };

  const clearResults = () => {
    resetForm();
    dispatch(clearSearchResults());
    dispatch(clearOrgSearchResults());
    setPage(0);
    setLastPage(0);
    setOrgPage(0);
    setOrgLastPage(0);
    setEntries(25);
    setOrgEntries(25);
  };

  useEffect(() => {
    clearResults();
  }, []);

  const onRowClick = (user: UserSearchResponse) => () => {
    dispatch(setSelectedUser(user));
    history.push(`${SCREEN_PATHS.UPDATE_USER}/${user.customerId}`);
  };

  const redirectToViewOrg = (orgId: string) => () => {
    history.push(`${SCREEN_PATHS.ORG_DETAILS}/${orgId}`);
  };

  const renderHeader = () => (
    <TableRow className={classes.tableHead}>
      <TableCell>User Name</TableCell>
      <TableCell>Organisation Name</TableCell>
      <TableCell>Phone Number</TableCell>
      <TableCell>Created Date</TableCell>
      <TableCell>Created Time</TableCell>
      <TableCell>Last Login</TableCell>
    </TableRow>
  );
  const renderRows = (entry: UserSearchResponse) => {
    const {
      username,
      orgName,
      phone,
      meta: { createdAt, lastLoginTime },
    } = entry;

    return (
      <TableRow key={entry.customerId} className={classes.tableRow}>
        <TableCell style={{ cursor: "pointer" }}>
          <Link onClick={onRowClick(entry)}>{username}</Link>
        </TableCell>
        <TableCell style={{ cursor: "pointer" }}>
          <Link onClick={redirectToViewOrg(entry.orgId)}>{orgName}</Link>
        </TableCell>
        <TableCell>{phone}</TableCell>
        <TableCell>
          {createdAt ? formatInTimeZone(createdAt, getUserTimeZone(), commonFormatDate) : "-"}
        </TableCell>
        <TableCell>
          {createdAt ? formatInTimeZone(createdAt, getUserTimeZone(), "HH:mm") : "-"}
        </TableCell>
        <TableCell>
          {lastLoginTime
            ? formatInTimeZone(lastLoginTime, getUserTimeZone(), commonFormatDate)
            : "-"}
        </TableCell>
      </TableRow>
    );
  };

  const renderEmptyDataRow = () => (
    <TableRow>
      <TableCell colSpan={6} align="center">
        {`No Matching User Located.`}
      </TableCell>
    </TableRow>
  );

  return (
    <Page title="Update User">
      <form onSubmit={handleSubmit}>
        <Box className={toolbarClasses.toolbar}>
          <Box className={toolbarClasses.topRow}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", flexGrow: 1 }}>
              <TextField
                className={toolbarClasses.search}
                size="small"
                name="username"
                placeholder="User name"
                disabled={isSubmitting}
                value={values.username}
                error={Boolean(touched.username && errors.username)}
                helperText={touched.username && errors.username}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ "aria-label": "Search by user name" }}
              />
              <TextField
                className={toolbarClasses.search}
                size="small"
                name="org"
                placeholder="Organisation name"
                disabled={isSubmitting}
                value={values.org}
                error={Boolean(touched.org && errors.org)}
                helperText={touched.org && errors.org}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ "aria-label": "Search by organisation name" }}
              />
            </Box>

            <Box className={toolbarClasses.searchGroup}>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                disabled={(!values.username && !values.org && !values.phone) || isSubmitting}>
                {isSubmitting ? "Searching..." : "Search"}
              </Button>
              <Button
                color="primary"
                variant="outlined"
                type="reset"
                disabled={isSubmitting}
                onClick={clearResults}>
                Clear
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
      <Box my={4}>
        <ScreenHeader sx={{ textAlign: "center" }} title="Search Results" />
        <Grid
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "flex-start",
            gap: "20px",
          }}>
          <Grid>
            <ScreenHeader sx={{ marginTop: "32px" }} title="User" />
            <Card elevation={12}>
              <Table
                data={results}
                renderHeader={renderHeader}
                renderRows={renderRows}
                renderEmptyDataRow={renderEmptyDataRow}
                page={page}
                entries={entries}
                onPageChange={setPage}
                onEntriesChange={handleOnEntriesChange}
                lastPage={lastPage}
              />
            </Card>
          </Grid>
          <Grid>
            <OrganisationList
              data={orgResults as OrganisationSearchedData}
              entries={orgEntries}
              setEntries={setOrgEntries}
              page={orgPage}
              lastPage={orgLastPage}
              setPage={setOrgPage}
              handleOnEntriesChange={handleOrgOnEntriesChange}
            />
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};

export default UpdateUserView;
